import React from "react";
import { Box, Typography, useTheme, IconButton, Stack } from "@mui/material";
import { tokens } from "../../../assets/theme";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";
import Diagram from "./diagram";
import { useQuery } from "@tanstack/react-query";
import { fetchMachinePace, fetchMachinePaceDetails } from "../../../fetchers/machinePace";
import CircularProgress from "@mui/material/CircularProgress";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { fetchMachineDetails } from "../../../fetchers/machine";;

export default function MachinePace(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [timeSpan, setTimeSpan] = useLocalStorage("timeSpan", 10);
    const { formatMessage } = useIntl();
    const machineId = props.machineId;
    const machine = props.machineQuery.data;
    const showCycleTime = machine?.showPaceAsCycleTime;
    const showPacePerMinute = machine?.speedUnitTypeId === 5;

    const machinePaceQuery = useQuery(
        ["machinePace", machineId, timeSpan, companyKey],
        () => fetchMachinePace(machineId, timeSpan, companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
        }
    );

    const productionOrderNo = machinePaceQuery.data?.activeProductionOrder;
    const machinePaceDetailsQuery = useQuery(
        ["machinePaceDetails", machineId, productionOrderNo, companyKey],
        () => fetchMachinePaceDetails(machineId, productionOrderNo, companyKey),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            enabled: !!productionOrderNo && productionOrderNo !== "#NA", // Only run this query when productionOrderNo is available
        }
    );

    const machineDetailsQuery = useQuery(
        ["machineDetails", companyKey, "currentshift", machine.machineName], 
        () =>
          fetchMachineDetails(companyKey, "currentshift", machine.machineName), // Så länge som vi bara är intresserad av planerad, rapporterad och kvarvarande kvantitet så kan vi ha currentshift här
        {
          staleTime: 60000,
          refetchInterval: 60000,
          enabled: !!machine,
          keepPreviousData: true
        }
      );

    const setCurrentCycleTimeBackgroundColor = (showCycleTime, avgCycleTimeTimespan, idealCycleTimeActive, avgPaceTimespan, idealPace) => {
        if (showCycleTime) {
            if (avgCycleTimeTimespan > idealCycleTimeActive) {
                return colors.redAccent[500];
            } else {
                return colors.dashbordGreen[600];
            } 
        } else {
            if (avgPaceTimespan < idealPace) {
                return colors.redAccent[500];
            } else {
                return colors.dashbordGreen[600];
            }
        }
    };

    if (machinePaceQuery.isLoading || machineDetailsQuery.isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={ props.tabExpanded ? "69vh" : "50vh"}>
                <CircularProgress color="secondary" />
            </Box>
        );
    }

    return (
        <Box width="100%" backgroundColor={colors.primary[400]} height={ props.tabExpanded ? "69vh" : "50vh"}>
            <Box
                display="grid"
                gridTemplateColumns="repeat(12, 1fr)"
                gap="20px"
                pt={2}
                pb={1}
                borderBottom={`2px solid ${colors.primary[500]}`}
            >
                <Box
                    gridColumn="span 2"
                    sx={{
                        backgroundColor: colors.primary[400]
                    }}
                >
                    <Box justifyContent="center" display="flex">
                        { showCycleTime ? <FormattedMessage {...messages.idealCycleTimeActive} /> : <FormattedMessage {...messages.idealPace} /> }
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex" >
                        { showCycleTime ? 
                            <Typography sx={{ fontSize: '2rem' }}>
                                { machine?.idealCycleTimeActive ? Number.parseFloat(machine?.idealCycleTimeActive).toFixed(2) : '-'} s
                            </Typography>
                            :
                            showPacePerMinute ? 
                             <Typography sx={{ fontSize: '2rem' }}>
                                { machinePaceQuery.data?.idealPace ? Math.round(60 / machine?.idealCycleTimeActive) : '-'}/min
                            </Typography>
                            :
                            <Typography sx={{ fontSize: '2rem' }}>
                                { machinePaceQuery.data?.idealPace ? Math.round(machinePaceQuery.data.idealPace) : '-'}/h
                            </Typography>
                        }
                    </Box>
                </Box>

                <Box
                    gridColumn="span 2"
                    sx={{
                        backgroundColor: colors.primary[400]
                    }}
                >
                    <Box justifyContent="center" display="flex">
                        { showCycleTime ? <FormattedMessage {...messages.currentCycleTime} /> : <FormattedMessage {...messages.currentPace} /> }
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex">
                        <Box backgroundColor={setCurrentCycleTimeBackgroundColor(showCycleTime, machinePaceQuery.data.avgCycleTimeTimespan, machine?.idealCycleTimeActive, machinePaceQuery.data.avgPaceTimespan, machinePaceQuery.data.idealPace)} borderRadius="2px" pr={2} pl={2}>
                            { showCycleTime ?
                                <Typography sx={{ fontSize: '2rem' }}>
                                    { machinePaceQuery.data?.avgCycleTimeTimespan ? Number.parseFloat(machinePaceQuery.data.avgCycleTimeTimespan).toFixed(2) : '-'} s
                                </Typography>
                                :
                                showPacePerMinute ?
                                <Typography sx={{ fontSize: '2rem' }}>
                                    { machinePaceQuery.data?.avgPaceTimespan ? Math.round(machinePaceQuery.data.avgPaceTimespan) : '-'}/min
                                </Typography>
                                :
                                <Typography sx={{ fontSize: '2rem' }}>
                                    { machinePaceQuery.data?.avgPaceTimespan ? Math.round(machinePaceQuery.data.avgPaceTimespan) : '-'}/h
                                </Typography>
                            }
                        </Box>
                    </Box>
                </Box>

                <Box
                    gridColumn="span 2"
                    sx={{
                        backgroundColor: colors.primary[400]
                    }}
                >
                    <Box alignContent="center" justifyContent="center" display="flex">
                        { showCycleTime ? <FormattedMessage {...messages.avgCycleTimeForPo} /> : <FormattedMessage {...messages.avgPaceForPo} /> }
                    </Box>
                    <Box alignItems="center" justifyContent="center" display="flex">
                        { showCycleTime ?
                            <Typography sx={{ fontSize: '2rem' }}>
                                { machinePaceQuery.data?.avgCycleTime ?  Number.parseFloat(machinePaceQuery.data.avgCycleTime).toFixed(2) : '-'} s
                            </Typography>
                            :
                            showPacePerMinute ?
                            <Typography sx={{ fontSize: '2rem' }}>
                                { machinePaceQuery.data?.avgPace ? Math.round(machinePaceQuery.data.avgPace) : '-'}/min
                            </Typography>
                            :
                            <Typography sx={{ fontSize: '2rem' }}>
                                { machinePaceQuery.data?.avgPace ? Math.round(machinePaceQuery.data.avgPace) : '-'}/h
                            </Typography>
                        }
                    </Box>
                </Box>
                <Box gridColumn="span 6" pr={5}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Box>
                            <FormattedMessage {...messages.timespanSelectorLabel} />
                            <RadioGroup
                                row
                                value={timeSpan}
                                onChange={(event) => setTimeSpan(Number(event.target.value))}
                            >
                                <FormControlLabel
                                    value={10}
                                    control={<Radio />}
                                    label={<Typography variant="body1">10 min</Typography>}
                                    sx={{
                                        '& .MuiRadio-root.Mui-checked': {
                                            color: colors.dashbordGreen[500],
                                        },
                                    }}
                                />
                                <FormControlLabel
                                    value={20}
                                    control={<Radio />}
                                    label={<Typography variant="body1">20 min</Typography>}
                                    sx={{
                                        '& .MuiRadio-root.Mui-checked': {
                                            color: colors.dashbordGreen[500],
                                        },
                                    }}
                                />
                                <FormControlLabel
                                    value={30}
                                    control={<Radio />}
                                    label={<Typography variant="body1">30 min</Typography>}
                                    sx={{
                                        '& .MuiRadio-root.Mui-checked': {
                                            color: colors.dashbordGreen[500],
                                        },
                                    }}
                                />
                                <FormControlLabel
                                    value={60}
                                    control={<Radio />}
                                    label={<Typography variant="body1">60 min</Typography>}
                                    sx={{
                                        '& .MuiRadio-root.Mui-checked': {
                                            color: colors.dashbordGreen[500],
                                        },
                                    }}
                                />
                            </RadioGroup>
                        </Box>
                    </Stack>    
                </Box>
            </Box>
            { machinePaceDetailsQuery.isLoading && productionOrderNo !== "#NA" && 
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <CircularProgress color="secondary" />
                </Box> 
            }
            { (!productionOrderNo || productionOrderNo === "#NA") && 
                <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                    <FormattedMessage {...messages.noActiveProductionOrder} />
                </Box> 
            }
            { machinePaceDetailsQuery.data &&
                <Diagram 
                    machinePaceDetailsQuery={machinePaceDetailsQuery} 
                    idealPace={machinePaceQuery.data?.idealPace} 
                    avgPace={machinePaceQuery.data?.avgPace} 
                    idealCycleTime={machine?.idealCycleTimeActive}
                    avgCycleTime={machinePaceQuery.data?.avgCycleTime}
                    showCycleTime={showCycleTime}
                    showPacePerMinute={showPacePerMinute}
                />
            }   
        </Box>
    );
}
